import React from "react";
import { Link } from "react-router-dom";
import { Grid } from "antd";

import { EnumTipoDeDocumento } from "@/Models/Domain/Pessoa/Enum";
import { TypeInscricaoDoAlunoNaViagem } from "@/Models/Domain/Viagem/InscricaoDoAlunoNaViagem";
import TablePaginadaBasic, { ITablePaginadaBasicProps } from "@/Components/Base/Table/TablePaginadaBasic";
import Conjunto from "@/Components/Conjunto";

import Formatacoes from "@/Utils/Formatacao";
import Utilitario from "@/Utils/Utilitario";
import { Balanco } from "@/Components/Balanco";
import { obterTagDoStatus, obterTagDoStatusString } from "@/Views/Viagem/Cadastros/Viagem/Inscricoes";
import GerarLinkPagamentoFormPopConfirm from "../LinkPagamentoInscricao/GerarLinkPagamentoFormPopConfirm";
type Dummy = {} // passar um tipo vázio para o DataIndex torna o processamento beem mais leve
type TableProps = ITablePaginadaBasicProps<TypeInscricaoDoAlunoNaViagem, Dummy>;

export const TodasInscricoesTableBasic: React.FC<{
    tableProps?: TableProps;
    expandColumns?: TableProps["columns"];
    actions?: (inscricao: TypeInscricaoDoAlunoNaViagem) => React.ComponentProps<typeof Conjunto>["itens"];
}> = (props) => {

    const breakpoint = Grid.useBreakpoint();

    const propsCsv: TableProps["csv"] = {
        enabled: true,
        filename: "inscricoes.csv",
        options: {
            emptyFieldValue: "",
        },
        acquireSource: !props?.tableProps?.csv?.enabled ? undefined : props.tableProps.csv.acquireSource,
        concat(dados) {
            return [
                {
                    header: "Gênero",
                    data: dados?.map((m) => m?.aluno?.pessoa?.generoExibicao ?? ""),
                },
                {
                    header: "Respónsavel",
                    data: dados?.map((m) => m?.responsvel?.nomeExibicao ?? ""),
                },
                {
                    header: "Res. CPF",
                    data: dados?.map((m) => m?.responsvel?.cpf ?? ""),
                },
                {
                    header: "Res. Email",
                    data: dados?.map((m) => m?.responsvel?.emails?.find((f) => f)?.endereco ?? ""),
                },
                {
                    header: "Res. Cel/Tel",
                    data: dados?.map(
                        (m) =>
                            `${m?.responsvel?.telefones?.find((f) => f)?.ddd ? "(" + m?.responsvel?.telefones?.find((f) => f)?.ddd + ") " : ""}${
                                m?.responsvel?.telefones?.find((f) => f)?.numero ?? ""
                            }`
                    ),
                },
                {
                    header: "Forma",
                    data: dados?.map((m) => m?.titulo?.formasPagamento?.findLast((f) => f)?.tipo?.nome ?? ""),
                },
                {
                    header: "DOCUMENTO",
                    data: dados?.map((m) => (m?.titulo?.formasPagamento?.findLast((f) => f) as any | undefined)?.identificadorProvedor ?? "-"),
                },
                {
                    header: "Dt. Pagamento",
                    data: dados?.map((m) => (m?.titulo?.dataPagamento ? Utilitario.dateToString(m.titulo.dataPagamento, "dd/MM/yyyy hh:mm:ss") : "-")),
                },
                {
                    header: "HISTÓRICO",
                    data: dados?.map((m) => ((m?.fatura?.descricao?.trim() ?? '') == '' ? ' - ' : m?.fatura?.descricao)),
                },
                {
                    header: "Ver",
                    data: dados?.map((m) => ((m.titulo?.codFatura ?? 0) > 0 ? `${window.location.origin}/financeiro/titulos/fatura/editar-ficha/${m.titulo?.codFatura}` : "")),
                },
            ];
        },
    };

    return (
        <React.Fragment>
            <TablePaginadaBasic<TypeInscricaoDoAlunoNaViagem, Dummy>
                {...props.tableProps}
                csv={propsCsv}
                bordered
                locale={{ emptyText: "Nenhuma inscrição encontrada." }}
                rowKey={(k) => k.codigo!}
                addTitleOn="md"
                fitDefined
                minGeneratedSpanWidth={breakpoint.lg ? 2 : breakpoint.sm ? 12 : 24}
                columns={
                    props.tableProps?.columns ??
                    ([
                        {
                            title: "#",
                            breakpoints: { xs: 12, md: 12, lg: 1 },
                            dataIndex: "codigo",
                            key: "inscricao-codigo",
                        },
                        {
                            title: "Viagem",
                            breakpoints: { xs: 24, sm: 12, lg: 4 },
                            dataIndex: ["viagem", "titulo"],
                            key: "inscricao-viagem-nome",
                            render: (v, r) => (
                                <Link target={"_blank"} to={`/viagens/viagens/detalhes?id=${r.codViagem}`}>
                                    {v}
                                </Link>
                            ),
                        },
                        {
                            title: "Aluno",
                            breakpoints: { xs: 24, sm: 12, lg: 3 },
                            dataIndex: ["aluno", "pessoa", "nomeExibicao"],
                            key: "inscricao-aluno-nome",
                            render: (_, r) => {
                                const nome = r?.aluno?.pessoa?.nome;
                                const nomeSocial = r?.aluno?.pessoa?.nomeSocial;

                                return (
                                    <Link target={"_blank"} to={`/viagens/alunos/editar?id=${r.codAluno}`}>
                                        {nomeSocial && nomeSocial != nome ? (
                                            <>
                                                {nomeSocial}
                                                <br/>
                                                ({nome})
                                            </>
                                        ) : (
                                            <>{nome}</>
                                        )}
                                    </Link>
                                );
                            },
                        },
                        {
                            title: "Documentos",
                            breakpoints: { xs: 24, sm: 12, lg: 4 },
                            noResponsiveTitle: true,
                            render: (_, record) => {
                                const documentos = [
                                    ["CPF", Formatacoes.formatarCPF(record.aluno?.pessoa?.cpf)],
                                    ["RNE",  record.aluno?.pessoa?.rne],
                                    ["Passaporte", record.aluno?.pessoa?.passaporte],
                                    [Utilitario.enumParaString(EnumTipoDeDocumento, record.aluno?.tipoDeDocumento), record.aluno?.documento],
                                ].filter((k) => k[1] != undefined);
                                
                                if (documentos.length == 0) return '';

                                return documentos.map(m => m[0] === undefined ? `Doc.: ${m[1]}` : m.join(": ")).join(" | ", );
                            },
                            key: "inscricao-aluno-documentoPrincipal",
                        },
                        {
                            title: "Nascimento",
                            breakpoints: { xs: 24, md: 12, lg: 3 },
                            dataIndex: ["aluno", "dataDeNascimento"],
                            key: "inscricao-aluno-nascimento",
                            render: (v) => Formatacoes.paraData(v)?.toLocaleDateString() ?? '',
                        },
                        {
                            title: "Status",
                            breakpoints: { xs: 24, sm: 12, lg: 3 },
                            dataIndex: ["status"],
                            render: obterTagDoStatus,
                            csv : {
                                get : (item) => item?.status === undefined ? '' : obterTagDoStatusString(item?.status)
                            },
                            key: "inscricao-viagem-status"
                        },
                        {
                            title: "Previsto",
                            breakpoints: { xs: 24, sm: 12, lg: 3 },
                            dataIndex: ["titulo", "valorPrevisto"],
                            key: "inscricao-titulo-valorPrevisto",
                            render: (v) => <Balanco> {Utilitario.toStringDinheiro(v) ?? '0'} </Balanco>,
                            csv : {
                                title: "Previsto (R$)",
                                get : (item) => Utilitario.toCSVDinheiro(item?.titulo?.valorPrevisto)
                            },
                        },
                        {
                            title: "Recebido",
                            breakpoints: { xs: 24, sm: 12, lg: 3 },
                            dataIndex: ["titulo", "valorRecebido"],
                            key: "inscricao-titulo-valorRecebido",
                            render: (v, r) => (
                                <React.Fragment>
                                    <Balanco> {Utilitario.toStringDinheiro(v) ?? '0'} </Balanco>
                                    {r.titulo?.codFatura && (
                                        <Link target={"_blank"} to={`/financeiro/titulos/fatura/editar-ficha/${r.titulo?.codFatura}`}>
                                            Ver
                                        </Link>
                                    )}
                                    <GerarLinkPagamentoFormPopConfirm 
                                        textButtonOpen="Ver ou gerar link"
                                        propsButtonOpen={{
                                            size : "small"
                                        }}
                                        inscricao={{
                                            ...r,
                                            viagem: r.viagem
                                        }}
                                    />
                                </React.Fragment>
                            ),
                            csv : {
                                get : (item) => Utilitario.toCSVDinheiro(item?.titulo?.valorRecebido)
                            },
                        },

                        ...(props.expandColumns ?? []),
                    ])
                }
            />
        </React.Fragment>
    );
}
